import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  ChefHat, Sparkles, ArrowRight, Check, Users, Globe,
  QrCode, Smartphone, Gift, TrendingUp, ShieldCheck,
  Clock, Star, Coffee, Utensils, Store, HandCoins,
} from "lucide-react";
import { useState } from "react";

const INCLUDED = [
  { icon: Globe, title: "Web pública del restaurante", body: "Tu negocio online con dominio `menugenius.online/r/tu-negocio`, menú digital, fotos y datos de contacto. Sin saber programar, lista en 5 minutos." },
  { icon: Users, title: "Fidelización de clientes", body: "Tarjeta de puntos digital, recompensas por visitas, registro por email o teléfono. Convierte clientes ocasionales en habituales." },
  { icon: QrCode, title: "QR del menú para las mesas", body: "Genera códigos QR únicos que apuntan a tu menú siempre actualizado. Imprímelos y pégalos en la barra o las mesas." },
  { icon: Utensils, title: "Menús ilimitados", body: "Crea todos los menús que necesites: carta principal, desayunos, menú del día… hasta 30 platos por menú." },
  { icon: ShieldCheck, title: "Ficha de alérgenos", body: "Cumple la normativa europea (UE 1169/2011). Edita la ficha manual de cada plato con los 14 alérgenos declarables." },
  { icon: Smartphone, title: "Panel desde tu móvil", body: "Gestiona clientes, fidelizaciones y menús desde cualquier dispositivo. Sin instalación, solo navegador." },
];

const TESTIMONIALS = [
  {
    name: "Laura G.",
    role: "Dueña, Café Central (Madrid)",
    text: "Teníamos Facebook y nada más. Con MenuGenius en una tarde ya teníamos nuestra web y 30 clientes registrados en fidelización. Por 9,99 € es un no-brainer.",
  },
  {
    name: "Jorge M.",
    role: "Propietario, El Rinconcito (Valencia)",
    text: "Lo más útil: el QR con el menú siempre actualizado. Cambio un precio y en 10 segundos está en la mesa. No más cartas impresas que quedan desactualizadas.",
  },
  {
    name: "Sara R.",
    role: "Responsable, Dulces Sueños (Sevilla)",
    text: "La fidelización nos ha funcionado muy bien. Clientes que venían una vez al mes ahora vienen cada semana por los puntos. Recuperamos el importe del plan varias veces.",
  },
];

const FAQ = [
  { q: "¿Puedo probar antes de pagar?", a: "Sí. Al registrarte tienes 14 días gratis con TODAS las funciones Pro (no solo Starter). Al acabar el trial, puedes quedarte en Starter por 9,99€, subir a Essentials (29€) o cancelar sin coste." },
  { q: "¿Qué pasa si mi negocio crece y necesito más funciones?", a: "Sube al plan Essentials (29€/mes) cuando quieras. Añadimos automáticamente contabilidad, OCR de facturas, inventario vivo y 440 créditos IA/mes. El cambio es inmediato y solo pagas la diferencia prorrateada." },
  { q: "¿Hay permanencia o comisiones ocultas?", a: "Ninguna. Cancela cuando quieras desde tu panel. Sin comisiones, sin SMS, sin TPV." },
  { q: "¿Necesito dominio propio?", a: "No. Te damos una URL pública (ej. menugenius.online/r/tu-negocio) que puedes compartir, imprimir en QR y enlazar desde Instagram. Si quieres dominio propio, está disponible en planes superiores." },
  { q: "¿Cuántos clientes fidelizados puedo tener?", a: "Hasta 100 clientes en Starter. Suficiente para un bar o cafetería pequeña. Si necesitas más, Essentials no tiene límite." },
  { q: "¿Me da factura?", a: "Sí, factura con IVA desglosado cada mes, automática desde Stripe." },
];

export default function StarterLandingPage() {
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const price = billingPeriod === "annual" ? "99" : "9,99";
  const period = billingPeriod === "annual" ? "año" : "mes";
  const saving = billingPeriod === "annual" ? "Ahorras 20,88€ al año" : "Equivale a 8,25€/mes si pagas anual";

  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="starter-landing-page">
      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b border-gray-200 bg-white/90 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 bg-primary rounded-xl flex items-center justify-center">
              <ChefHat className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-semibold text-lg">MenuGenius</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/"><Button variant="ghost" size="sm">Volver</Button></Link>
            <Link to="/register">
              <Button size="sm" data-testid="starter-nav-cta">
                Empezar gratis 14 días <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-white to-amber-50" aria-hidden />
        <div className="relative max-w-6xl mx-auto px-6 pt-20 pb-16 lg:pt-28 lg:pb-24 text-center">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-100 text-amber-900 border border-amber-200 mb-6" data-testid="starter-hero-badge">
            <Sparkles className="h-3.5 w-3.5" /> Plan Starter · Para bares, cafeterías y food trucks
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]" data-testid="starter-hero-title">
            Tu bar en internet por{" "}
            <span className="bg-gradient-to-br from-orange-500 to-amber-600 bg-clip-text text-transparent">
              9,99€ al mes
            </span>
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
            Web pública + Fidelización de clientes + Menú con QR. Sin saber de ordenadores. Sin permanencia. Montado en una tarde.
          </p>
          <div className="mt-9 flex flex-wrap gap-3 justify-center">
            <Link to="/register">
              <Button size="lg" className="text-base px-7" data-testid="starter-hero-cta-primary">
                Empezar 14 días gratis <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </Link>
            <a href="#pricing">
              <Button size="lg" variant="outline" className="text-base px-7" data-testid="starter-hero-cta-secondary">
                Ver qué incluye
              </Button>
            </a>
          </div>
          <p className="mt-4 text-xs text-gray-500">
            Sin tarjeta durante el trial · Cancela en un clic · Factura con IVA
          </p>

          {/* Trust strip */}
          <div className="mt-14 grid grid-cols-3 gap-6 max-w-2xl mx-auto text-sm">
            <div className="flex flex-col items-center gap-1.5">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-orange-700" />
              </div>
              <p className="font-semibold">5 minutos</p>
              <p className="text-xs text-gray-500">para estar online</p>
            </div>
            <div className="flex flex-col items-center gap-1.5">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <HandCoins className="w-5 h-5 text-emerald-700" />
              </div>
              <p className="font-semibold">Sin TPV</p>
              <p className="text-xs text-gray-500">ni comisiones</p>
            </div>
            <div className="flex flex-col items-center gap-1.5">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Star className="w-5 h-5 text-amber-700" />
              </div>
              <p className="font-semibold">4,8/5</p>
              <p className="text-xs text-gray-500">de hosteleros</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── QUIÉN LO NECESITA ──────────────────────────────────────── */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto text-center mb-12">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">
            Pensado para negocios que empiezan
          </h2>
          <p className="text-lg text-gray-600">
            Si tu negocio encaja con alguno de estos, el plan Starter es para ti.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
          {[
            { Icon: Coffee, label: "Cafeterías" },
            { Icon: Utensils, label: "Bares de tapas" },
            { Icon: Store, label: "Food trucks" },
            { Icon: Gift, label: "Pastelerías / heladerías" },
          ].map((x) => (
            <div key={x.label} className="bg-white rounded-2xl border border-gray-200 p-6 text-center hover:shadow-md transition" data-testid={`starter-persona-${x.label.toLowerCase().replace(/[ /]+/g, "-")}`}>
              <div className="w-12 h-12 mx-auto rounded-xl bg-orange-100 flex items-center justify-center mb-3">
                <x.Icon className="w-6 h-6 text-orange-700" />
              </div>
              <p className="font-semibold">{x.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── QUÉ INCLUYE ───────────────────────────────────────────── */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">
              Todo esto, por 9,99€ al mes
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              No es una versión "castrada" — es lo que de verdad necesitas para estar online y fidelizar clientes.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {INCLUDED.map((item, i) => (
              <Card key={i} className="border border-gray-200 hover:border-orange-300 hover:shadow-md transition-all" data-testid={`starter-feature-${i}`}>
                <CardContent className="p-6">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center mb-4">
                    <item.icon className="w-5 h-5 text-orange-700" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ── CÓMO FUNCIONA ─────────────────────────────────────────── */}
      <section className="py-20 px-6 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">
              En 5 minutos estás online
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { n: "1", title: "Regístrate gratis", body: "Email y contraseña. Sin tarjeta. Tus 14 días de prueba empiezan con todo el pack Pro desbloqueado." },
              { n: "2", title: "Sube tu menú", body: "Teclea los platos o importa un PDF/foto de tu carta actual — la IA extrae los platos automáticamente." },
              { n: "3", title: "Comparte tu web", body: "Tendrás un enlace tipo menugenius.online/r/tu-negocio. Genera el QR y empieza a fidelizar." },
            ].map((s) => (
              <div key={s.n} className="text-center" data-testid={`starter-step-${s.n}`}>
                <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-orange-500 to-amber-600 text-white font-bold text-2xl flex items-center justify-center mb-4 shadow-lg">
                  {s.n}
                </div>
                <h3 className="font-semibold text-xl mb-2">{s.title}</h3>
                <p className="text-sm text-gray-700 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING (una sola card) ──────────────────────────────── */}
      <section id="pricing" className="py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">
              Un solo precio, sin sorpresas
            </h2>
            <p className="text-gray-600">Empieza gratis, cancela cuando quieras.</p>
          </div>

          {/* Toggle */}
          <div className="flex items-center justify-center gap-2 mb-10" data-testid="starter-billing-toggle">
            <button
              type="button"
              onClick={() => setBillingPeriod("monthly")}
              data-testid="starter-toggle-monthly"
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${billingPeriod === "monthly" ? "bg-orange-500 text-white shadow" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            >
              Mensual
            </button>
            <button
              type="button"
              onClick={() => setBillingPeriod("annual")}
              data-testid="starter-toggle-annual"
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${billingPeriod === "annual" ? "bg-orange-500 text-white shadow" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            >
              Anual
              <span className="ml-2 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] rounded-full font-bold">-17%</span>
            </button>
          </div>

          <div className="relative bg-white border-2 border-orange-300 rounded-3xl p-8 sm:p-12 shadow-xl" data-testid="starter-pricing-card">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-orange-500 to-amber-600 text-white text-xs font-bold rounded-full uppercase tracking-wide">
              Plan Starter
            </div>
            <div className="text-center">
              <div className="flex items-baseline justify-center gap-2 mb-1">
                <span className="text-6xl sm:text-7xl font-extrabold text-gray-900">{price}€</span>
                <span className="text-xl text-gray-500">/ {period} + IVA</span>
              </div>
              <p className="text-sm text-orange-700 font-medium mb-8">{saving}</p>

              <ul className="text-left space-y-3 max-w-md mx-auto mb-10">
                {[
                  "Web pública con URL compartible",
                  "Menú digital con QR para las mesas",
                  "Fidelización de hasta 100 clientes",
                  "Ficha de alérgenos (UE 1169/2011)",
                  "Hasta 30 platos por menú",
                  "Soporte por email",
                  "14 días gratis con el pack Pro completo",
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{f}</span>
                  </li>
                ))}
              </ul>

              <Link to="/register">
                <Button size="lg" className="w-full max-w-md text-base py-6 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white shadow-lg" data-testid="starter-pricing-cta">
                  Empezar 14 días gratis <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </Link>
              <p className="mt-4 text-xs text-gray-500">
                Al acabar el trial puedes quedarte con Starter o subir a Essentials (29€) · Sin compromiso
              </p>
            </div>
          </div>

          {/* Upsell row */}
          <p className="text-center text-sm text-gray-500 mt-10">
            ¿Necesitas contabilidad, OCR de facturas e inventario?{" "}
            <Link to="/#pricing" className="text-orange-700 hover:text-orange-700 font-semibold underline underline-offset-4" data-testid="starter-upsell-essentials">
              Mira el plan Essentials
            </Link>
          </p>
        </div>
      </section>

      {/* ── TESTIMONIOS ──────────────────────────────────────────── */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">
              Negocios que ya están online
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Card key={i} className="border border-gray-200" data-testid={`starter-testimonial-${i}`}>
                <CardContent className="p-6">
                  <div className="flex gap-0.5 mb-4">
                    {[0, 1, 2, 3, 4].map((s) => (
                      <Star key={s} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed mb-5">“{t.text}”</p>
                  <div>
                    <p className="font-semibold text-sm">{t.name}</p>
                    <p className="text-xs text-gray-500">{t.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────── */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">Preguntas frecuentes</h2>
          </div>
          <div className="space-y-4">
            {FAQ.map((f, i) => (
              <details key={i} className="group bg-white border border-gray-200 rounded-2xl p-5 hover:border-orange-300 transition" data-testid={`starter-faq-${i}`}>
                <summary className="cursor-pointer font-semibold text-gray-900 flex items-center justify-between">
                  {f.q}
                  <span className="text-orange-500 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                </summary>
                <p className="mt-3 text-sm text-gray-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ────────────────────────────────────────────── */}
      <section className="py-20 px-6 bg-gradient-to-br from-orange-500 to-amber-600 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <TrendingUp className="w-10 h-10 mx-auto mb-5 opacity-90" />
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
            Empieza hoy. Gratis durante 14 días.
          </h2>
          <p className="text-lg opacity-95 mb-8 max-w-xl mx-auto">
            Sin tarjeta, sin permanencia. Solo 9,99€/mes cuando decidas seguir.
          </p>
          <Link to="/register">
            <Button size="lg" variant="secondary" className="text-base px-8 bg-white text-orange-700 hover:bg-gray-100" data-testid="starter-final-cta">
              Crear mi cuenta gratis <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-6 border-t border-gray-200 text-center text-sm text-gray-500">
        <p>© 2026 MenuGenius · <Link to="/" className="hover:text-gray-900">Inicio</Link> · <Link to="/#pricing" className="hover:text-gray-900">Todos los planes</Link></p>
      </footer>
    </div>
  );
}
